import { useJoinStateStore } from '@/stores/cansino/joinState';
import { useUserStore } from '~/stores/user';
import type { GameHall, TabItem } from './gameTps';

/**
 * 检查是否在服务器端渲染
 * @returns 如果是服务器端渲染，返回true
 */
const isServerSide = (): boolean => import.meta.server;

/**
 * 获取大厅列表
 * @returns 大厅列表数组
 */
export const getHallsList = (): GameHall[] => {
    if (isServerSide()) return [];
    
    try {
        const joinStateStore = useJoinStateStore();
        const userStore = useUserStore();
        const oNowCurrency = userStore.oNowCurrency; 
        const balance = Number(oNowCurrency.balance); 
        
        // 添加空值检查，防止访问未定义的属性
        if (!joinStateStore.config) {
            console.warn('getHallsList: config not found');
            return [];
        }
        
        const halls = joinStateStore.config.halls || {};
        // 创建新对象存储处理后的数据，避免修改 store 中的原始数据
        const processedHalls: Record<string, any> = {};
        const currentCurrency = userStore.$state.currentCurrency;
        const hallLimits = joinStateStore.config[currentCurrency]?.hallLimit || {};
        
        // 合并逻辑：将限制规则添加到各厅信息中
        for (const hallType in halls) {
            if (hallLimits[hallType]) {
                const hallCurrentLimit = hallLimits[hallType];
                
                // hallCurrentLimit 是嵌套结构 { x3: {...}, x5: {...}, x10: {...} }
                // 取所有倍率中最低的门槛来判断是否可进入
                let minEntry = Infinity;
                let minBet = Infinity;
                
                for (const rateKey of ['x3', 'x5', 'x10']) {
                    const rateConfig = hallCurrentLimit[rateKey];
                    if (rateConfig) {
                        minEntry = Math.min(minEntry, Number(rateConfig.minimumEntry) || 0);
                        minBet = Math.min(minBet, Number(rateConfig.minBet) || 0);
                    }
                }
                
                // 将处理后的数据存储到新对象中，而不是修改原始 halls
                processedHalls[hallType] = {
                    ...halls[hallType],          // 保留原有属性
                    ...hallCurrentLimit,         // 添加x3/x5/x10嵌套配置
                    accessable: (minEntry !== Infinity && balance >= minEntry), // 根据最低门槛判断是否可进入
                    betable: (minBet !== Infinity && balance >= minBet),        // 根据最低门槛判断是否可下注
                };
            }
        }
        
        // 输出处理后的结果
        return Object.values(processedHalls);
    } catch (error) {
        console.error('Error in getHallsList:', error);
        return [];
    }
}

/**
 * 获取游戏桌子列表
 * @returns 游戏桌子数组
 */
export const getTables = (): any[] => {
    if (isServerSide()) return [];

    try {
        const joinStateStore = useJoinStateStore();
        
        // 添加空值检查，防止访问未定义的属性
        if (!joinStateStore.config) {
            console.warn('getTables: config not found');
            return [];
        }
        
        const tables = joinStateStore.config.tables || {};
        
        // 安全地将表格对象转换为数组
        if (tables && typeof tables === 'object') {
            return Object.values(tables);
        }
        return [];
    } catch (error) {
        console.error('Error in getTables:', error);
        return [];
    }
}

/**
 * 获取倍率列表
 * @returns 倍率列表数组
 */
export const getMultipliers = (): TabItem[] => {
    if (isServerSide()) return [];
    
    try {
        const joinStateStore = useJoinStateStore();
        
        // 添加空值检查，防止访问未定义的属性
        if (!joinStateStore.config) {
            console.warn('getMultipliers: config not found');
            return [];
        }
        
        const multipliers = joinStateStore.config.multipliers || {};
        
        // 仅在倍率存在且为对象时进行转换
        if (multipliers && typeof multipliers === 'object') {
            return convertToArray(multipliers);
        }
        return [];
    } catch (error) {
        console.error('Error in getMultipliers:', error);
        return [];
    }
}

/**
 * 将对象转换为数组格式
 * @param obj 要转换的对象
 * @returns 转换后的数组
 */
const convertToArray = (obj: Record<string, any>): TabItem[] => {
    try {
        return Object.entries(obj).map(([k, v]) => ({
            name: k,
            value: Object.entries(v).map(([n, val]) => ({name: n, value: val}))
        }));
    } catch (error) {
        console.error('Error in convertToArray:', error);
        return [];
    }
};

/**
 * 获取游戏配置
 * 统一入口调用多个接口获取所需数据
 * @returns 包含大厅、桌子和倍率的对象
 */
export const getGameConfig = async (): Promise<{ halls: GameHall[], tables: any[], multipliers: TabItem[] }> => {
    if (isServerSide()) return { halls: [], tables: [], multipliers: [] };
    
    try {
        // 并行请求所有数据以提高效率
        const [halls, tables, multipliers] = await Promise.all([
            getHallsList(),
            getTables(),
            getMultipliers()
        ]);
        
        return { halls, tables, multipliers };
    } catch (error) {
        console.error('Error in getGameConfig:', error);
        return { halls: [], tables: [], multipliers: [] };
    }
};
// ↓ 跳转到游戏入口页 
export const fnGoToEntrance = (
    oConfig={},
)=>{
    const path = `/casino/niuniuGame/entrance`; 
    useRouter().push({
        path: getLocalPath(path),
        replace: true, // 默认不生成历史记录  
        ...oConfig,
    });
}


/**
 * 进入某个厅的方法
 */
export const fnGoIntoOneHall = (
    oOneHall,
    rate,
    oConfig={},
)=>{
    if (!oOneHall.enabled) {
        showNegativeNotify({
            i18n: 'goldenFlower.hallDisabled',
        }); 
        return;
    }
    if (oOneHall['accessable'] == false){
        showNegativeNotify({
            i18n: 'niuniu.lowBalance',
        }); 
        return;
    }
    const path = `/casino/niuniuGame/indoor/${rate}`; 
    useRouter().push({
        path: getLocalPath(path),
        // replace: true, // 不生成历史记录 
        ...oConfig,
    });
}